import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// Chakra imports
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDescription,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Table,
  Tbody,
  Icon,
  Text,
  Th,
  Thead,
  Tr,
  SimpleGrid,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
  Center,
  Spinner,
  Select,
  Input,
  Textarea,
} from "@chakra-ui/react";

// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// Table Components
import TablesProjectRow from "components/Tables/TablesProjectRow";
import TablesTableRow from "components/Tables/TablesTableRow";

// Data
import { tablesProjectData, tablesTableData } from "variables/general";

// Icons
import { AiFillCheckCircle } from "react-icons/ai";
import { useHistory, useLocation } from "react-router-dom";
import { getFileTempLink } from "service/kyc.service";
import { useFormik } from "formik";
import * as yup from "yup";
import GradientBorder from "components/GradientBorder/GradientBorder";
import { verifyUser } from "service/kyc.service";
import { releasePaymentUser } from "service/kyc.service";

function KycDetail() {
  const history = useHistory();

  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [kycUser, setKycUser] = useState(location?.state ?? null);
  const [imagesLink, setImagesLink] = useState(null);
  const [paymentSending, setPaymentSending] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const [verificationStatusString, setVericationStatusString] = useState(
    "approved user send payment 20"
  );

  const cancelRef = useRef();

  const colorBGStatus = {
    approved: "green.400",
    failed: "red.400",
    processing: "orange.400",
    "approved user send payment 20": "cyan.600",
  };

  const formik = useFormik({
    initialValues: {
      verification_status: kycUser.know_your_client.verification_status ?? "",
      admin_message: kycUser.know_your_client.admin_message ?? "",
      //payment
      // user_id: kycUser.
    },
    validationSchema: yup.object().shape({
      verification_status: yup.string().required("required"),
      admin_message: yup.string().when("verification_status", {
        is: (val) => val === "failed",
        then: () => yup.string().required("required"),
        otherwise: () => yup.string(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        await verifyUser(kycUser.id, values);
        history.push("/admin/kyc");
      } catch (error) {
        formik.setErrors({ ...error });
      }
    },
  });

  const releasePayment = async (user_id, verification_status) => {
    try {
      const data = {
        user_id: user_id,
        verification_status: verification_status,
      };
      const kycData = await releasePaymentUser(data);

      if (kycData) {
        return true;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPaymentSending(false);
    }
  };

  const getFileLink = async (id) => {
    {
      setImagesLink((prev) => {
        return {
          ...prev,
          url: null,
          loading: true,
        };
      });
      const link = await getFileTempLink({ file_id: id, user_id: kycUser.id });
      if (link)
        setImagesLink((prev) => {
          return {
            ...prev,
            url: link,
            loading: false,
          };
        });
    }
  };

  useEffect(() => {
    if (isOpen && imagesLink && imagesLink.id) {
      getFileLink(imagesLink.id);
    }
  }, [isOpen]);

  useEffect(() => {
    if (kycUser === null) history.push("/admin/kyc");
  }, []);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* Authors Table */}
      {kycUser && kycUser.know_your_client && (
        <>
          <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
            <CardHeader p="6px 0px 22px 0px">
              <Text fontSize="lg" color="#fff" fontWeight="bold">
                {kycUser.know_your_client.id_name ?? "User"} Information
              </Text>
            </CardHeader>
            <CardBody w={"full"}>
              <SimpleGrid columns={2} w={"full"}>
                <VStack alignItems={"start"} my={"3"}>
                  <Text color={"white"}>Name</Text>
                  <Text color={"white"}>{kycUser.know_your_client.id_name}</Text>
                </VStack>
                <VStack alignItems={"start"} my={"3"}>
                  <Text color={"white"}>Identification Number </Text>
                  <Text color={"white"}>{kycUser.know_your_client.id_number}</Text>
                </VStack>
                <Box>
                  <CardHeader p="6px 0px 22px 0px">
                    <Text fontSize="md" color="#fff" fontWeight="bold" my={"2"}>
                      {kycUser.know_your_client.id_name} Files
                    </Text>
                  </CardHeader>
                  {kycUser.know_your_client.files.map((file) => (
                    <Text
                      key={file.id}
                      color={"white"}
                      cursor={"pointer"}
                      textDecoration={"underline"}
                      onClick={() => {
                        onOpen();
                        setImagesLink({ id: file.id, url: null, loading: true });
                      }}
                    >
                      {file.name}
                    </Text>
                  ))}
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
          <Card overflowX={{ sm: "scroll", xl: "hidden" }} my={"6"}>
            <CardHeader p="6px 0px 22px 0px">
              <Text fontSize="md" color="#fff" fontWeight="bold" my={"2"}>
                Bank Information
              </Text>
            </CardHeader>
            <SimpleGrid columns={2} w={"full"}>
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Bank Name</Text>
                <Text color={"white"}>{kycUser?.bank_infos[0]?.bank_name ?? ""}</Text>
              </VStack>
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Bank Account Name </Text>
                <Text color={"white"}>{kycUser?.bank_infos[0]?.holder_name ?? ""}</Text>
              </VStack>
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Bank Account Number </Text>
                <Text color={"white"}>{kycUser?.bank_infos[0]?.account_no ?? ""}</Text>
              </VStack>
            </SimpleGrid>
          </Card>
          <Card overflowX={{ sm: "scroll", xl: "hidden" }} my={"6"}>
            <CardHeader p="6px 0px 22px 0px">
              <Text fontSize="md" color="#fff" fontWeight="bold" my={"2"}>
                Admin Decision
              </Text>
            </CardHeader>
            <VStack alignItems={"start"} my={"3"}>
              <Text color={"white"}>Application Status</Text>
              <Text
                p={"4"}
                rounded={"xl"}
                color={"white"}
                bg={colorBGStatus[kycUser.know_your_client.verification_status]}
              >
                {kycUser.know_your_client.verification_status}
              </Text>
            </VStack>
            <FormControl
              isInvalid={
                formik.errors.verification_status && formik.touched.verification_status
              }
            >
              <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                Update status
              </FormLabel>
              <GradientBorder
                mb="24px"
                w={{ base: "100%", lg: "fit-content" }}
                borderRadius="20px"
              >
                <Select
                  color="white"
                  bg={colorBGStatus[formik.values.verification_status]}
                  border="transparent"
                  borderRadius="20px"
                  fontSize="sm"
                  size="lg"
                  w={{ base: "100%", md: "346px" }}
                  maxW="100%"
                  h="46px"
                  // placeholder="Update User Application Status"
                  // _placeholder={{color:"black"}}
                  {...formik.getFieldProps("verification_status")}
                >
                  <option style={{color:"black"}} value={"failed"}>failed</option>
                  <option style={{color:"black"}} value={"processing"}>processing</option>
                  <option style={{color:"black"}} value={"approved"}>approved</option>
                  <option style={{color:"black"}} value={"approved user send payment 20"}>
                    approved user send payment 20
                  </option>
                </Select>
              </GradientBorder>
              <FormErrorMessage>{formik.errors.verification_status}</FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={formik.errors.admin_message && formik.touched.admin_message}
            >
              <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                Justification
              </FormLabel>
              <GradientBorder mb="24px" w={"full"} borderRadius="20px">
                <Textarea
                  w={"full"}
                  color="white"
                  bg="rgb(19,21,54)"
                  border="transparent"
                  borderRadius="20px"
                  fontSize="sm"
                  size="lg"
                  maxW="100%"
                  h="46px"
                  placeholder="Justify Your update to User To help User Get Verified"
                  {...formik.getFieldProps("admin_message")}
                />
              </GradientBorder>
              <FormErrorMessage>{formik.errors.admin_message}</FormErrorMessage>
            </FormControl>
            <Center>
              <Button
                variant="brand"
                fontSize="10px"
                type="submit"
                w="100%"
                maxW="350px"
                h="45"
                mb="20px"
                mt="20px"
                mx={"2"}
                isLoading={formik.isSubmitting}
                onClick={formik.submitForm}
              >
                Submit
              </Button>
              <Button
                disabled={
                  kycUser.know_your_client.verification_status !=
                  "approved"
                }
                variant="brand"
                fontSize="10px"
                type="submit"
                w="100%"
                maxW="350px"
                h="45"
                mb="20px"
                mt="20px"
                mx={"2"}
                textTransform={"uppercase"}
                fontWeight={"bold"}
                backgroundColor={"green.500"}
                isLoading={paymentSending}
                onClick={() => {
                  window.confirm("Are you sure?")
                  
                  setPaymentSending(true);

                  releasePayment(kycUser?.id, verificationStatusString);

                  setPaymentComplete(true)

                  setKycUser(location?.state ?? null)
                }}
              >
                Release payment
              </Button>
            </Center>
          </Card>
        </>
      )}

      <>
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Customer
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure? You can't undo this action afterwards.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={onClose} ml={3}>
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </>

      <Modal isOpen={paymentComplete}>
        <ModalBody>Done!</ModalBody>
      </Modal>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setImagesLink(null);
          onClose();
        }}
        size={"3xl"}
        minH={"500px"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          bg={
            "linear-gradient(10.84deg, rgba(80, 80, 80, 0.80) 59.3%, rgba(26, 31, 55, 0) 100%)"
          }
        >
          {/* <ModalHeader>Modal Title</ModalHeader> */}
          {/* <ModalCloseButton /> */}
          <ModalBody>
            {imagesLink?.loading ? (
              <Center>
                <Spinner />
              </Center>
            ) : imagesLink?.url ? (
              <Center>
                <Image alt={"user file"} src={imagesLink?.url?.link} />
              </Center>
            ) : (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Oops link expired!</AlertTitle>
                <AlertDescription>Please try again later</AlertDescription>
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default KycDetail;
